/* eslint-disable @typescript-eslint/member-ordering */

import { NgComponentOutlet } from '@angular/common';
import { Component, computed, DOCUMENT, effect, EffectRef, inject, Signal, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { CustomerModel } from '@libs/account/models';
import { AdvertisingModalContentComponent, CmsSkeletonComponent, InfoBarContentComponent } from '@libs/cms/components';
import { AppConfigModel } from '@libs/cms/models';
import { DesktopNavigationComponent, ScrollTopComponent, SideSheetComponent } from '@libs/common/components';
import { HeaderComponent, NavbarComponent } from '@libs/components/cep';
import { FooterComponent } from '@libs/features/common';
import { AuthService } from '@libs/shared/auth';
import { AppStateService } from '@libs/shared/common';
import { APP_THEME_CLASS_MAP, AppThemeClass, AppThemeName, NavModel } from '@libs/shared/models';
import { AppConfigService, NavigationService, ViewportService } from '@libs/shared/services';
import { GtmService } from '@libs/shared/ui';
import { AgentStatusBarComponent } from '@libs/templates/cep';
import {
  CEP_HOST_OPTIONS,
  CEP_HOST_SLOTS,
  CepHostOptions,
  CepHostSlots,
} from '../cep-host.model';

@Component({
  imports: [
    NgComponentOutlet,
    RouterModule,
    HeaderComponent,
    DesktopNavigationComponent,
    InfoBarContentComponent,
    FooterComponent,
    CmsSkeletonComponent,
    AdvertisingModalContentComponent,
    NavbarComponent,
    AgentStatusBarComponent,
    SideSheetComponent,
    ScrollTopComponent,
  ],
  selector: 'ui-cep-host',
  templateUrl: './cep-host.component.html',
  styleUrls: ['./cep-host.component.scss', './cep-host.component.lekkerland-2026.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.Emulated,
})
export class CepHostComponent {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly stateService: AppStateService = inject(AppStateService);
  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly doc: Document = inject(DOCUMENT);
  private readonly gtmService: GtmService = inject(GtmService);
  private readonly themeClasses: AppThemeClass[] = Object.values(APP_THEME_CLASS_MAP);
  private initializedGtmId: string | undefined;

  public readonly slots: CepHostSlots = inject(CEP_HOST_SLOTS, { optional: true }) ?? {};
  public readonly options: CepHostOptions = inject(CEP_HOST_OPTIONS, { optional: true }) ?? {};

  public readonly mainNavigation: Signal<NavModel | undefined> = toSignal(
    this.navigationService.getMainNavigation(),
    { initialValue: undefined },
  );

  public readonly showDesktopNavigation: Signal<boolean> = computed((): boolean =>
    this.isDesktop() && !!this.mainNavigation()?.children?.length,
  );

  public readonly isDesktop: Signal<boolean> = toSignal(
    this.viewportService.isDesktop(),
    { initialValue: true },
  );

  public readonly config: Signal<AppConfigModel | undefined> = toSignal(
    this.appConfigService.getConfig(),
    { initialValue: undefined },
  );

  public readonly theme: Signal<AppThemeName> = toSignal(
    this.appConfigService.getTheme(),
    { initialValue: 'LL24' },
  );

  public readonly themeClass: Signal<string> = computed((): string => {
    const theme: AppThemeName = this.theme();

    return APP_THEME_CLASS_MAP[theme];
  });

  private readonly syncThemeClass: EffectRef = effect((): void => {
    const themeClass: string = this.themeClass();

    this.doc.documentElement.classList.remove(...this.themeClasses);
    this.doc.documentElement.classList.add(themeClass);
  });

  public readonly customer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;

  private readonly syncPageState: EffectRef = effect((): void => {
    const customer: CustomerModel | undefined = this.customer();

    this.stateService.set('page', {
      rootId: '0',
      customerNumber: customer?.id,
      useAppMainNavigation: true,
    });
  });

  private readonly initializeGtm: EffectRef = effect((): void => {
    const gtmId: string | undefined = this.config()?.settings.gtmId;

    if (gtmId && gtmId !== this.initializedGtmId) {
      this.initializedGtmId = gtmId;
      this.gtmService.initialize(gtmId);
    }
  });
}
